import React, { useEffect, useRef, useState, useMemo } from "react";
import CommonHeader from "../../components/common-header";
import * as css from "./style.module.less";
import weEmpty from "./assets/we-empty.svg";
import editIcon from "../../assets/edit.svg";
import { Tooltip, Input, Button, message } from "@cloud/atom-ui";
import classNames from "classnames";
import { deleteWeAbility, updateWeAppInfo, fetchWeAppInfo } from "./server";
import { useDispatch } from "redux-react-hook";
import useMappedState from "@src/utils/useMappedState";
import * as actions from "@src/actions/index";
import history from "@src/utils/history";
import { queryString, getAppPathFromType } from "@src/utils/common";
import config from "../../../../configs/config";
import { abilityType } from '../../utils/utils'

const applicationWe = () => {
  const { appInfo } = useMappedState("root");
  const dispatch = useDispatch();
  const [empty, setEmpty] = useState(false);
  const [appConfig, setAppConfig] = useState({
    version: "",
    publishBy: "",
    uploadTime: "",
    pcHomeUrl: "",
    managementUrl: ""
  });
  const [editing, setEditing] = useState({
    pcHomeUrl: {
      isEdit: false,
      value: ""
    },
    managementUrl: {
      isEdit: false,
      value: ""
    }
  });
  const pcHomeUrlEditRef = useRef(null);
  const managementUrlEditRef = useRef(null);
  const pageInitRef = useRef(false);
  const type = queryString("type");

  useEffect(() => {
    if (appInfo === undefined || pageInitRef.current) {
      return;
    }
    if (!appInfo?.abilityList?.includes(abilityType.wecode)) {
      history.push(
        `/${type}/appdetailV2/addAppCapabilities?id=${appInfo?.aliasName}&type=${type}`
      );
      return;
    }
    fetchWeAppInfo(appInfo.aliasName, getAppPathFromType(type))
      .then(res => {
        pageInitRef.current = true;
        if (res.status === 1) {
          const {
            versionCode,
            managerUrlCn,
            pcUrlCn,
            uploadTime,
            uploadUser
          } = res.data || {};
          if (!versionCode) {
            setEmpty(true);
            return;
          }
          setAppConfig({
            version: versionCode,
            publishBy: uploadUser,
            uploadTime: uploadTime,
            pcHomeUrl: pcUrlCn,
            managementUrl: managerUrlCn
          });
        } else {
          message.error(res.errMsg || "查询应用功能详情失败");
        }
      })
      .catch(err => {
        console.log(err);
        pageInitRef.current = true;
        setEmpty(true);
        message.error("查询应用功能详情失败");
      });
  }, [appInfo]);

  const contentRender = () => {
    if (!pageInitRef.current) {
      return null;
    }
    if (empty) {
      return (
        <div className={css["empty"]}>
          <img src={weEmpty} alt="" />
          <p>
            <span>暂无版本，请先通过IDE上传We码包，</span>
            <span
              onClick={() => {
                window.open(config.IDEGuildUrl, "_blank");
              }}
            >
              IDE使用指南
            </span>
          </p>
        </div>
      );
    }

    return (
      <div className={css["we-config"]}>
        {configText("version", "版本号", appConfig.version, false, false)}
        {configText("publishBy", "发布者", appConfig.publishBy, true, false)}
        {configText(
          "uploadTime",
          "上传时间",
          appConfig.uploadTime,
          false,
          false
        )}
        {configText("pcHomeUrl", "PC端地址", appConfig.pcHomeUrl, false, true)}
        {configText(
          "managementUrl",
          "管理后台地址",
          appConfig.managementUrl,
          false,
          true
        )}
      </div>
    );
  };

  useEffect(() => {
    Object.keys(editing).forEach(item => {
      if (editing[item].isEdit) {
        if (item === "pcHomeUrl") {
          pcHomeUrlEditRef.current?.focus?.();
        } else {
          managementUrlEditRef.current?.focus?.();
        }
      }
    });
  }, [editing]);

  /**
   * @param {string} configKey editing的key值
   */
  const editInputSave = configKey => {
    const reg = new RegExp(/^(http:\/\/|https:\/\/)/);
    const param = {
      pcHomeUrl: appConfig.pcHomeUrl,
      managementUrl: appConfig.managementUrl
    };

    param[configKey] = editing[configKey].value;
    if (param[configKey] && !reg.test(param[configKey])) {
      message.warning("地址无效，请重新输入");
      return;
    };
    updateWeAppInfo(
      {
        pcUrlCn: param.pcHomeUrl,
        manageUrlCn: param.managementUrl,
        appId: appInfo.aliasName
      },
      getAppPathFromType(type)
    )
      .then(res => {
        if (res.status === 1) {
          setAppConfig({
            ...appConfig,
            [configKey]: editing[configKey].value
          });
          editValueCancel(configKey, "", false);
        } else {
          message.warning(res.errMsg || "保存失败，稍后重试！");
        }
      })
      .catch(err => {
        console.log(err);
        message.warning("保存失败，稍后重试！");
      });
  };

  /**
   * @description 编辑输入框交互
   * @param {"pcHomeUrl"|"managementUrl"} configKey
   * @param {string} value 输入框的值
   * @param {boolean} isEdit 编辑框的状态
   */
  const editValueCancel = (configKey, value, isEdit) => {
    setEditing({
      ...editing,
      [configKey]: {
        isEdit,
        value
      }
    });
  };

  /**
   * @description 删除微码应用能力
   */
  const deleteAbility = () => {
    deleteWeAbility(appInfo.aliasName, getAppPathFromType(type))
      .then(res => {
        if (res.status === 1) {
          dispatch(
            actions.setAppInfo({
              ...appInfo,
              abilityList: appInfo.abilityList?.filter(i => i !== abilityType.wecode)
            })
          );
          history.push(
            `/${type}/appdetailV2/addAppCapabilities?id=${appInfo?.aliasName}&type=${type}`
          );
        } else {
          message.warning(res.errMsg || "删除微码应用能力失败");
        }
      })
      .catch(err => {
        console.log(err);
        message.warning("删除微码应用能力失败");
      });
  };

  const valueEditRender = (configKey, value) => {
    if (Object.keys(editing).includes(configKey) && editing[configKey].isEdit) {
      return (
        <div className={css["value-edit"]}>
          <Input
            ref={
              configKey === "pcHomeUrl"
                ? pcHomeUrlEditRef
                : managementUrlEditRef
            }
            className={css["value-edit-input"]}
            defaultValue={editing[configKey].value}
            onChange={e => editValueCancel(configKey, e.target.value, true)}
          />
          <Button
            type="primary"
            className={classNames(
              css["value-edit-btn"],
              css["value-edit-save"]
            )}
            onClick={() => editInputSave(configKey)}
          >
            保存
          </Button>
          <Button
            className={css["value-edit-btn"]}
            onClick={() => editValueCancel(configKey, "", false)}
          >
            取消
          </Button>
        </div>
      );
    } else {
      return (
        <div className={css["value-outer"]}>
          <p className={css["value-inner"]}>
            {value}
          </p>
          <img
            className={css["edit-icon"]}
            src={editIcon}
            alt=""
            onClick={() => editValueCancel(configKey, value, true)}
          />
        </div>
      );
    }
  };

  const configText = (configKey, label, value, showMore, edit) => {
    let valueDom = <span className={css["value"]}>{value}</span>;
    if (showMore) {
      valueDom = (
        <Tooltip title={value}>
          <span className={css["value"]}>{value}</span>
        </Tooltip>
      );
    }
    // 是否支持编辑模式
    if (edit) {
      valueDom = valueEditRender(configKey, value);
    }
    return (
      <div className={css["config-list"]}>
        <span className={css["label"]}>{label}</span>
        {valueDom}
      </div>
    );
  };

  return (
    <div className={css["container"]}>
      <CommonHeader
        headerStyle={{ marginBottom: "24px" }}
        title="we码"
        showRightBtn={true}
        btnClick={() => deleteAbility()}
        desc={[
          "通过简单的前端语法即可写出Native级别的性能体验，支持iOS和Android端部署"
        ]}
      />
      {contentRender()}
    </div>
  );
};

export default applicationWe;
